'use client';

import { useTranslation } from 'next-i18next';
import { Anchor, Button, Tooltip } from '@components/common';
import { InformationCircleIcon, ShareIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import { cn } from '@utils/cn';
import { useBjModal } from '@hooks/common/useBjModal';
import { ShareModal } from '@components/features/modals/share-modal/ShareModal';
import { getI18nLinkHref } from '@services/i18nPaths';

interface JobAttributes {
  slug?: string;
  active?: boolean;
  expiresIn: number;
  numberOfApplicants?: number;
  numberOfViews?: number;
  activationInfo?: ApiSchema<'MyJobCardActivationInfo'>;
  title?: string;
}

interface StatusBarProps<T extends JobAttributes> {
  job: T;
  hasShare?: boolean;
  locale: Locale;
}

/**
 * @description Compute the color of the status dot
 * - Gray for inactive jobs
 * - Green for active jobs with more than 7 days until expiration
 * - Yellow for active jobs with 7 days or less until expiration
 * @param active - Status of the job. Required.
 * @param expiresIn - The number of days the job expires in. Required.
 */
const computeDotColor = (
  active: JobAttributes['active'],
  expiresIn: JobAttributes['expiresIn'],
) => {
  if (!active) return 'bg-ink-light';
  return expiresIn > 7 ? 'bg-secondary' : 'bg-warning';
};

export const StatusBar = <T extends JobAttributes>(
  props: StatusBarProps<T>,
) => {
  const { job, hasShare = false, locale } = props;
  const { t } = useTranslation('common');
  const totalViews = job?.numberOfViews || 0;

  const { isOpen: isOpenShareModal, toggleModal: toggleShareModal } = useBjModal();

  return (
    <>
      <div className="mb-1 flex items-center gap-2">
        <span
          className={cn(
            'size-2.5 rounded-full shrink-0',
            computeDotColor(job.active, job.expiresIn),
          )}
        />

        <p className="grow text-tiny text-ink-medium">
          {job.active
            ? `${t('my-jobs.card.status.active')}: ${t(
              'my-jobs.card.expires-in',
              { expiresIn: job.expiresIn },
            )}`
            : t('my-jobs.card.status.inactive')}
          {' ('}
          {typeof job.numberOfApplicants !== 'undefined' && job.slug && (
            <Anchor
              href={`/job/${job.slug}/applicants/flow`}
              styling="none"
              className={cn('hover:underline', !job.numberOfApplicants && 'pointer-events-none')}
            >
              {t('my-jobs.card.number-of-applicants', {
                numberOfApplicants: job.numberOfApplicants,
              })}
              ,
            </Anchor>
          )}

          {' '}
          {t('job.viewers.page.total_views', { totalViews })}
          )
        </p>

        <div className="flex shrink-0 items-center gap-x-2">
          {job.activationInfo && (
            <Tooltip
              tooltipText={(
                <div className="flex flex-col gap-2 p-2">
                  <h6 className="border-b pb-2 text-left text-sm font-bold">
                    {`${t('my-jobs.promotion')} - ${
                      job.activationInfo.promotionType
                    }`}
                  </h6>
                  <div className="flex flex-col gap-1 text-ink-medium">
                    {job.activationInfo.benefits?.map((benefit) => (
                      <p
                        key={benefit}
                        className="flex justify-between gap-2 text-left text-xs leading-4"
                      >
                        <span>{benefit}</span>
                        <CheckIcon className="size-4 shrink-0 text-secondary" />
                      </p>
                    ))}
                  </div>
                  <h6 className="mt-4 border-b pb-2 text-left text-sm font-bold">
                    {t('my-jobs.active-period')}
                  </h6>
                  <div className="flex justify-between gap-6 text-xs text-ink-medium">
                    <p>{t(`my-jobs.card.status.${job.activationInfo.status}`)}</p>
                    <p>
                      {
                        job.activationInfo.status === 'active'
                          ? job.activationInfo.activeTill
                          : job.activationInfo.lastActivePeriod
                      }
                    </p>
                  </div>
                </div>
              )}
              floating
              useFloatingPortal
              className="inline-flex w-fit"
            >
              <InformationCircleIcon
                className={cn(
                  'size-5 text-ink-light',
                  job.active && 'text-secondary',
                )}
              />
            </Tooltip>
          )}

          {hasShare && job.slug && job.title && (
            <Tooltip
              tooltipText={t('job.detail.share.modal.title')}
              position="top"
              colorScheme="black"
              floating
              useFloatingPortal
            >
              <Button
                color="light"
                className="!p-2"
                size="sm"
                rounding="full"
                onClick={toggleShareModal}
              >
                <ShareIcon className="size-5" />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {hasShare && job.slug && job.title && (
        <ShareModal
          isOpen={isOpenShareModal}
          onClose={toggleShareModal}
          ogTitle={job.title}
          ogDescription={job.title}
          pathnameShareUrl={`${getI18nLinkHref(`/job/${job.slug}`, locale)}`}
        />
      )}
    </>
  );
};
